import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import type {
  AdjuntoOut,
  ComentarioCreate,
  ComentarioOut,
  TicketCounters,
  TicketCreate,
  TicketDetail,
  TicketStateChange,
  TicketUpdate,
  TicketsFilters,
  TicketsListResponse,
} from "./schemas";

const KEY = "tickets";

// ── Helper para construir searchParams ─────────────────────────────────
function buildSearchParams(f: TicketsFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: f.page ?? 1,
    per_page: f.per_page ?? 25,
  };
  if (f.estado) params.estado = f.estado;
  if (f.estado_grupo) params.estado_grupo = f.estado_grupo;
  if (f.categoria) params.categoria = f.categoria;
  if (f.prioridad) params.prioridad = f.prioridad;
  if (f.asignado_a) params.asignado_a = f.asignado_a;
  if (f.asignados_a_mi) params.asignados_a_mi = true;
  if (f.sin_asignar) params.sin_asignar = true;
  if (f.cliente_id) params.cliente_id = f.cliente_id;
  if (f.busqueda) params.busqueda = f.busqueda;
  return params;
}

// ── Queries ────────────────────────────────────────────────────────────
//
// Polling automático — `refetchInterval` mantiene la lista y el detail
// frescos sin que el operador tenga que F5. Se pausa con
// `refetchIntervalInBackground: false` cuando la pestaña pierde foco
// (no spammeamos el API si nadie está mirando).
//
// `refetchOnWindowFocus: true` (default de TanStack) también dispara
// un refetch inmediato al volver al tab — combinado con interval pa'
// que al regresar de mucho rato veas todo al instante.

export function useListTickets(filters: TicketsFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () =>
      apiCall<TicketsListResponse>("get", "/api/v1/tickets", {
        searchParams: buildSearchParams(filters),
      }),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000, // 30s — lista al día sin F5
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
}

export function useTicket(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<TicketDetail>("get", `/api/v1/tickets/${id}`),
    enabled: !!id,
    refetchInterval: 20_000, // 20s — comentarios nuevos llegan solitos
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

/**
 * Counters para badge del sidebar y headers del topbar.
 * Refetch cada 60s en background — el badge se mantiene fresh sin spam.
 */
export function useTicketCounters() {
  return useQuery({
    queryKey: [KEY, "counters"],
    queryFn: () => apiCall<TicketCounters>("get", "/api/v1/tickets/counters"),
    refetchInterval: 60_000, // 1 min
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────
/// Invalida TODAS las queries de tickets (list, detail, counters). Usado
/// internamente en mutations y también exportado pa' refrescar manualmente
/// después de operaciones que no pasan por una mutation (ej. upload de
/// adjuntos via fetch directo).
export function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [KEY] });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TicketCreate) =>
      apiCall<TicketDetail>("post", "/api/v1/tickets", { json: data }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TicketUpdate) =>
      apiCall<TicketDetail>("patch", `/api/v1/tickets/${id}`, { json: data }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAssignTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asignado_a: string | null) =>
      apiCall<TicketDetail>("post", `/api/v1/tickets/${id}/assign`, {
        json: { asignado_a },
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useChangeTicketState(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TicketStateChange) =>
      apiCall<TicketDetail>("post", `/api/v1/tickets/${id}/state`, { json: data }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddComentario(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ComentarioCreate) =>
      apiCall<ComentarioOut>("post", `/api/v1/tickets/${ticketId}/comentarios`, {
        json: data,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * Upload de adjunto a un ticket. v1.4.0 — multipart/form-data.
 * Si `comentarioId` se pasa, el adjunto se liga al comentario; si no,
 * queda como "adjunto inicial" del ticket.
 *
 * Limita: max 10 MB. Mime whitelist: image/* + application/pdf.
 */
export async function uploadAdjuntoTicket(
  ticketId: string,
  file: File,
  comentarioId?: string,
): Promise<AdjuntoOut> {
  const fd = new FormData();
  fd.append("archivo", file);
  if (comentarioId) fd.append("comentario_id", comentarioId);

  // Token directo desde el store — ky con FormData puede meter Content-Type
  // wrong y rompe el boundary. Usamos fetch nativo aquí.
  const token = useAuthStore.getState().accessToken;
  const resp = await fetch(`/api/v1/tickets/${ticketId}/adjuntos`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      detail = body.detail ?? body.error?.message ?? detail;
    } catch {
      /* body no-JSON */
    }
    throw new Error(detail);
  }
  return (await resp.json()) as AdjuntoOut;
}
